import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrderDetailCreate } from "@cold-crew/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface OrderProductSummary {
  id: number;
  nombre: string;
  cantidad: number;
  precio_unitario: number;
}

interface Producto {
  id: number;
  nombre: string;
  precio_venta: number;
  categoria: string;
}

interface OrderDetailFormProps {
  value: OrderDetailCreate[];
  onChange: (lines: OrderDetailCreate[]) => void;
  disabled?: boolean;
  onSummaryChange?: (items: OrderProductSummary[]) => void;
}

export function OrderDetailForm({
  value,
  onChange,
  disabled,
  onSummaryChange,
}: OrderDetailFormProps) {
  const { data: productosData } = useQuery({
    queryKey: ["productos-bebida"],
    queryFn: () =>
      api.get<{ data: Producto[]; total: number }>(
        "/inventario/productos",
        { categoria: "bebida", activo: "1", limit: "50" }
      ),
    staleTime: 5 * 60 * 1000,
  });

  const productos: Producto[] = Array.isArray(productosData)
    ? productosData
    : productosData?.data ?? [];

  function emit(next: OrderDetailCreate[]) {
    onChange(next);

    if (onSummaryChange) {
      const summary = next
        .filter((line) => line.producto_id && line.cantidad > 0)
        .map((line) => {
          const producto = productos.find((p) => p.id === line.producto_id);
          return {
            id: line.producto_id,
            nombre: producto?.nombre ?? "Producto",
            cantidad: line.cantidad || 0,
            precio_unitario: line.precio_unitario || 0,
          };
        });
      onSummaryChange(summary);
    }
  }

  function getLine(productoId: number) {
    return value.find((line) => line.producto_id === productoId);
  }

  function getCantidad(productoId: number) {
    return getLine(productoId)?.cantidad ?? 0;
  }

  function setCantidad(producto: Producto, cantidad: number) {
    const existente = getLine(producto.id);

    if (cantidad <= 0) {
      emit(value.filter((line) => line.producto_id !== producto.id));
      return;
    }

    if (existente) {
      emit(
        value.map((line) =>
          line.producto_id === producto.id
            ? { ...line, cantidad, precio_unitario: producto.precio_venta }
            : line
        )
      );
      return;
    }

    emit([
      ...value,
      {
        producto_id: producto.id,
        cantidad,
        precio_unitario: producto.precio_venta,
      },
    ]);
  }

  if (productos.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[#d8d3ca] bg-[#fbfaf7] px-4 py-5 text-center">
        <p className="text-[12px] font-semibold text-[#8b867e]">
          No hay productos disponibles.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {productos.map((producto) => {
        const cantidad = getCantidad(producto.id);
        const seleccionado = cantidad > 0;

        return (
          <div
            key={producto.id}
            className={cn(
              "min-h-[150px] rounded-xl border bg-white px-4 py-5 transition-all",
              seleccionado
                ? "border-[#171614] shadow-[0_2px_0_#171614]"
                : "border-[#ddd8cf] hover:border-[#aaa49a]"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="truncate text-[18px] font-black leading-tight text-[#171614]">
                  {producto.nombre}
                </h4>
                <p className="mt-1 text-[12px] font-semibold tabular-nums text-[#8b867e]">
                  ${Number(producto.precio_venta || 0).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-2">
              <button
                type="button"
                disabled={disabled || cantidad === 0}
                onClick={() => setCantidad(producto, cantidad - 1)}
                className="flex size-9 items-center justify-center rounded-full border border-[#ddd8cf] bg-white text-[#171614] transition-colors hover:bg-[#f2eee7] disabled:cursor-not-allowed disabled:opacity-30"
                aria-label={`Quitar ${producto.nombre}`}
              >
                <Minus className="size-4" strokeWidth={2.5} />
              </button>

              <span className="min-w-6 text-center text-[16px] font-black tabular-nums text-[#171614]">
                {cantidad}
              </span>

              <button
                type="button"
                disabled={disabled}
                onClick={() => setCantidad(producto, cantidad + 1)}
                className="flex size-9 items-center justify-center rounded-full bg-[#171614] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
                aria-label={`Agregar ${producto.nombre}`}
              >
                <Plus className="size-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

